<script setup lang="ts" generic="T extends string | number | null">
  import { useFocus } from '@vueuse/core';
  import { onBeforeMount, ref, useTemplateRef } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import { cn } from '@ui/utils/utils';

  import type { InputVariants } from '.';
  import { inputVariants } from '.';

  export interface InputProps {
    class?: HTMLAttributes['class'];
    defaultValue?: any;
    placeholder?: string;
    size?: InputVariants['size'];
    trim?: boolean;
    variant?: InputVariants['variant'];
  }

  const props = defineProps<InputProps>();

  const modelValue = defineModel<T>();

  const focused = ref(false);
  const textInput = useTemplateRef<HTMLInputElement>('textInput');
  const { focused: textInputFocus } = useFocus(textInput);

  onBeforeMount(() => {
    if (modelValue.value === undefined && props.defaultValue !== undefined) {
      modelValue.value = props.defaultValue;
    }
  });

  defineExpose({
    focus: () => textInput.value?.focus(),
    focusDocumentNumberInput: () => textInput.value?.focus(),
    focusNumberInput: () => textInput.value?.focus(),
  });
</script>

<template>
  <input
    ref="textInput"
    :placeholder
    data-slot="input"
    :value="modelValue"
    :class="
      cn(
        'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input h-9 w-full min-w-0 rounded-sm border bg-background px-3 py-1 text-base transition-color outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 text-xs',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[2px]',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        inputVariants({ size, variant }),
        props.class,
      )
    "
    @input="
      (e) => {
        if (e.target) {
          modelValue = (e.target as HTMLInputElement).value as T;
        }
      }
    "
    @blur="
      (e) => {
        if (e.target) {
          let value = (e.target as HTMLInputElement).value as T;
          if (trim && (typeof value === 'string' || value instanceof String)) {
            value = value.trim() as T;
            // Update the DOM directly so the trimmed value is visible immediately,
            // without recreating the element (which would use a stale prop value).
            (e.target as HTMLInputElement).value = String(value ?? '');
          }

          if (value !== modelValue) {
            modelValue = value;
          }
        }
      }
    "
  />
</template>
